/**
 * 原型渲染基座（售前 AI 知识库 v2.6 · outputs/售前AI知识库-ui-prototype-v2.6.html）。
 *
 * 这里的 ICON / F / 身份矩阵都是**从原型逐字导出**，不手工重写：
 * 前端一旦自己维护一份副本，就会出现「原型一套、实现一套」的老问题（见 v1.7 审查报告）。
 * 权限码与 registry/perm-registry.json 的 115 个码完全一致，因此显隐判定可直接用注册表。
 */

export const F = {
  assetView: 'asset.view', assetDownload: 'asset.download', assetCreate: 'asset.create',
  assetMeta: 'asset.update_metadata', assetArchive: 'asset.archive', assetMove: 'asset.move',
  folderCreate: 'folder.create', uploadRevision: 'asset.upload_revision', previewOriginal: 'revision.preview_original',
  diffProcess: 'revision.diff_process', review: 'revision.review', approve: 'revision.approve',
  publish: 'revision.publish', rollback: 'revision.rollback', withdraw: 'revision.withdraw',
  quarantine: 'revision.quarantine', restore: 'revision.restore', renew: 'revision.renew',
  descUpdate: 'content.description.update', pageDescUpdate: 'content.page_description.update', noteCreate: 'content.note.create',
  noteResolve: 'content.note.resolve', pageSearch: 'page.search', pageQuote: 'page.quote',
  pageExport: 'page.export', kbAsk: 'kb.ask', kbFeedback: 'kb.feedback',
  kbTrace: 'kb.trace.view', rfpUpload: 'rfp.upload', rfpAnalyze: 'rfp.analyze',
  reqConfirm: 'requirement.confirm', recConfirm: 'recommendation.confirm', projectCreate: 'project.create',
  projectView: 'project.view', projectEdit: 'project.edit', projectManage: 'project.manage',
  projectMembers: 'project.manage_members', projectAi: 'project.ai_analyze', pagePlan: 'project.page_plan.edit',
  assemble: 'project.assemble', projectFreeze: 'project.freeze', projectDownload: 'project.download',
  projectArchive: 'project.archive', projectRestore: 'project.restore', favView: 'favorite.view',
  favCreate: 'favorite.create', favDelete: 'favorite.delete', favFolder: 'favorite.folder.manage',
  deliveryView: 'delivery.view', deliveryDownload: 'delivery.download', deliveryApprove: 'delivery.approve',
  opsTaskView: 'ops.task.view', opsTaskRetry: 'ops.task.retry', opsTaskCancel: 'ops.task.cancel',
  opsDeadletter: 'ops.deadletter.manage', opsRetention: 'ops.retention.manage', opsExpiry: 'ops.expiry.manage',
  opsEval: 'ops.eval.manage', opsCost: 'ops.cost.view', cdReq: 'ops.compliance_delete.request',
  cdApprove: 'ops.compliance_delete.approve', holdManage: 'ops.legal_hold.manage', auditView: 'audit.view',
  auditExport: 'audit.export', idUser: 'identity.user.manage', idRole: 'identity.role.manage',
  accessGrant: 'access.grant', accessView: 'access.view', accessPreview: 'access.preview',
  accessBreak: 'access.break_glass', aiProvider: 'ai.provider.manage', aiSecretMeta: 'ai.secret.view_metadata',
  aiSecretRotate: 'ai.secret.rotate', aiRoute: 'ai.route.manage', aiCost: 'ai.cost.view',
  aiAudit: 'ai.audit.view', systemManage: 'system.manage', storageManage: 'storage.manage',
  assetManage: 'asset.manage', folderView: 'folder.view', folderUpdate: 'folder.update',
  folderMove: 'folder.move', folderDelete: 'folder.delete', noteRead: 'content.note.read',
  noteUpdate: 'content.note.update', noteResolve2: 'content.note.resolve', revExpireScan: 'revision.expire_scan',
  redactionCreate: 'redaction.derivative.create', redactionApprove: 'redaction.derivative.approve', crosslangPair: 'crosslang.pair.manage',
  moduleManage: 'module.manage', modulePublish: 'knowledge.module.publish', baselinePublish: 'knowledge.baseline.publish',
  baselineUpgrade: 'project.baseline.upgrade', confirmExpired: 'project.page_plan.confirm_expired', kbToProject: 'kb.answer.add_to_project',
  projectOwnerTransfer: 'project.owner_transfer', projectFolderManage: 'project.folder.manage', projectFileManage: 'project.file.manage',
  projectDelete: 'project.delete', manualInsertSubmit: 'ppt.manual_insert.submit', manualInsertVerify: 'ppt.manual_insert.verify',
  attrRetry: 'ppt.attribute_check.retry', idGroup: 'identity.group.manage', idPolicy: 'identity.policy.manage',
  accessRevoke: 'access.revoke', accessTemp: 'access.approve_temporary', svcAccount: 'integration.service_account.manage',
  aiProviderView: 'ai.provider.view', aiModelManage: 'ai.model.manage', aiModelView: 'ai.model.view',
  aiHealth: 'ai.health_check', aiRouteView: 'ai.route.view', aiSecretCreate: 'ai.secret.create',
  aiSecretDisable: 'ai.secret.disable', aiSecretRecover: 'ai.secret.manual_recover',
} as const

export const ICON: Record<string, string> = {
  library: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/>',
  review: '<path d="m9 11 3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  project: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>',
  kb: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"/>',
  ops: '<path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/><path d="M12 12l3 3"/><path d="M12 12V8"/>',
  delivery: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="M9 15h6"/>',
  favorite: '<path d="M12 17.3 6.2 21l1.6-6.6L2.5 9.8l6.8-.6L12 3l2.7 6.2 6.8.6-5.3 4.6 1.6 6.6Z"/>',
  accounts: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.9"/>',
  models: '<path d="M12 8V4"/><rect x="4" y="8" width="16" height="12" rx="2"/><path d="M2 14h2M20 14h2M12 14v-2"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>',
  file: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/>',
  alert: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/>',
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
  rotate: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.9"/>',
  key: '<circle cx="7.5" cy="15.5" r="4.5"/><path d="m10.5 12.5 8-8 3 3-2 2-2-2-2 2"/>',
  list: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>',
  module: '<path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/>'
}



export function icon(name: string, size = 16): string {
  return '<svg class="ico" width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">' + (ICON[name] ?? '') + '</svg>'
}

/** 与原型 permAttr 一致：声明权限码，由 ProtoPage 在渲染后统一裁决显隐。 */
export function perm(code: string): string {
  return ' data-perm="' + code + '"'
}

export interface DesignRole { key: string; name: string; avatar: string; lens: string }

/** 原型 §1.4 的 11 个业务身份；lens 指向注册表里服务端使用的角色名（SR 端裁决）。 */
export const DESIGN_ROLES: DesignRole[] = [
  { key: 'owner', name: '内容负责人', avatar: '内', lens: 'content_owner' },
  { key: 'reviewer', name: '审核人', avatar: '审', lens: 'reviewer' },
  { key: 'bd', name: 'BD', avatar: 'BD', lens: 'bd' },
  { key: 'pm', name: '项目负责人', avatar: '项', lens: 'project_owner' },
  { key: 'auditor', name: '安全审计员', avatar: '审', lens: 'auditor' },
  { key: 'viewer', name: '只读用户', avatar: '读', lens: 'viewer' },
  { key: 'admin', name: '系统管理员', avatar: '管', lens: 'system_admin' },
  { key: 'librarian', name: '素材管理员', avatar: '素', lens: 'librarian' },
  { key: 'iam', name: '身份与权限管理员', avatar: '权', lens: 'iam_admin' },
  { key: 'deliveryReviewer', name: '交付审核人', avatar: '交', lens: 'delivery_reviewer' },
  { key: 'svc', name: '服务账号（集成）', avatar: '服', lens: 'svc' },
]

export const LENS_ORDER = DESIGN_ROLES.map((r) => r.lens)
